import React, { useState, useEffect } from 'react';
import { collection, getDocs, updateDoc, deleteDoc, doc, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase';
import Header from './Header';
import './Manager.css';

function FeedbackManager() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);

  useEffect(() => {
    loadFeedbacks();
  }, []);

  const loadFeedbacks = async () => {
    try {
      const q = query(collection(db, 'feedbacks'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const feedbacksData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setFeedbacks(feedbacksData);
    } catch (error) {
      console.error('Error loading feedbacks:', error);
      alert('Failed to load feedbacks');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (feedbackId, newStatus) => {
    try {
      await updateDoc(doc(db, 'feedbacks', feedbackId), {
        status: newStatus,
        updatedAt: new Date().toISOString(),
      });
      alert('Feedback status updated successfully!');
      loadFeedbacks();
    } catch (error) {
      console.error('Error updating feedback:', error);
      alert('Failed to update feedback status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this feedback?')) return;

    try {
      await deleteDoc(doc(db, 'feedbacks', id));
      alert('Feedback deleted successfully!');
      loadFeedbacks();
    } catch (error) {
      console.error('Error deleting feedback:', error);
      alert('Failed to delete feedback');
    }
  };

  const viewDetails = (feedback) => {
    setSelectedFeedback(feedback);
    setReplyText(feedback.adminReply || '');
    setShowModal(true);
  };

  const handleReply = async () => {
    if (!replyText.trim()) {
      alert('Please enter a reply message');
      return;
    }

    try {
      setSubmittingReply(true);
      await updateDoc(doc(db, 'feedbacks', selectedFeedback.id), {
        adminReply: replyText.trim(),
        repliedAt: new Date().toISOString(),
        repliedBy: 'admin',
        status: 'resolved', // Automatically mark as resolved when replied
        updatedAt: new Date().toISOString(),
      });

      alert('Reply sent successfully! User will see it in their app.');
      setShowModal(false);
      setReplyText('');
      loadFeedbacks();
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('Failed to send reply. Please try again.');
    } finally {
      setSubmittingReply(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';

    // Check if it's a Firestore Timestamp
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleString();
    }

    // Check if it's an ISO string
    if (typeof timestamp === 'string') {
      return new Date(timestamp).toLocaleString();
    }

    return 'N/A';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return '#FFA500';
      case 'reviewed':
        return '#2196F3';
      case 'resolved':
        return '#4CAF50';
      default:
        return '#666';
    }
  };

  const filteredFeedbacks = filterStatus === 'all'
    ? feedbacks
    : feedbacks.filter(f => f.status === filterStatus);

  return (
    <div>
      <Header />

      <div className="container">
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Feedback Management</h2>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <label htmlFor="statusFilter">Filter by Status:</label>
              <select
                id="statusFilter"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All ({feedbacks.length})</option>
                <option value="pending">Pending ({feedbacks.filter(f => f.status === 'pending').length})</option>
                <option value="reviewed">Reviewed ({feedbacks.filter(f => f.status === 'reviewed').length})</option>
                <option value="resolved">Resolved ({feedbacks.filter(f => f.status === 'resolved').length})</option>
              </select>
            </div>
          </div>

          {loading && <p>Loading...</p>}

          {!loading && filteredFeedbacks.length === 0 && (
            <p>No feedback found.</p>
          )}

          {!loading && filteredFeedbacks.length > 0 && (
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Reply</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredFeedbacks.map((feedback) => (
                  <tr key={feedback.id}>
                    <td>{formatDate(feedback.createdAt)}</td>
                    <td>{feedback.name}</td>
                    <td>{feedback.email}</td>
                    <td>
                      <span
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor: '#f0f0f0',
                          fontSize: '12px',
                        }}
                      >
                        {feedback.category}
                      </span>
                    </td>
                    <td>
                      <select
                        value={feedback.status}
                        onChange={(e) => handleStatusUpdate(feedback.id, e.target.value)}
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          backgroundColor: getStatusColor(feedback.status),
                          color: '#fff',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                        }}
                      >
                        <option value="pending">Pending</option>
                        <option value="reviewed">Reviewed</option>
                        <option value="resolved">Resolved</option>
                      </select>
                    </td>
                    <td>
                      {feedback.adminReply ? (
                        <span
                          style={{
                            padding: '4px 8px',
                            borderRadius: '12px',
                            backgroundColor: '#4CAF50',
                            color: '#fff',
                            fontSize: '11px',
                            fontWeight: '600',
                          }}
                        >
                          ✓ Replied
                        </span>
                      ) : (
                        <span
                          style={{
                            padding: '4px 8px',
                            borderRadius: '12px',
                            backgroundColor: '#FF9800',
                            color: '#fff',
                            fontSize: '11px',
                            fontWeight: '600',
                          }}
                        >
                          No Reply
                        </span>
                      )}
                    </td>
                    <td className="actions">
                      <button
                        onClick={() => viewDetails(feedback)}
                        className="btn btn-secondary"
                        style={{ marginRight: '8px' }}
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleDelete(feedback.id)}
                        className="btn btn-danger"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && selectedFeedback && (
        <div className="modal" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Feedback Details</h2>
              <button
                className="close-btn"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <div style={{ marginBottom: '15px' }}>
                <strong>Name:</strong>
                <p style={{ marginTop: '5px' }}>{selectedFeedback.name}</p>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <strong>Email:</strong>
                <p style={{ marginTop: '5px' }}>
                  <a href={`mailto:${selectedFeedback.email}`}>{selectedFeedback.email}</a>
                </p>
              </div>

              {selectedFeedback.phone && (
                <div style={{ marginBottom: '15px' }}>
                  <strong>Phone:</strong>
                  <p style={{ marginTop: '5px' }}>
                    <a href={`tel:${selectedFeedback.phone}`}>{selectedFeedback.phone}</a>
                  </p>
                </div>
              )}

              <div style={{ marginBottom: '15px' }}>
                <strong>Category:</strong>
                <p style={{ marginTop: '5px' }}>{selectedFeedback.category}</p>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <strong>Status:</strong>
                <p style={{ marginTop: '5px' }}>
                  <span
                    style={{
                      padding: '4px 12px',
                      borderRadius: '4px',
                      backgroundColor: getStatusColor(selectedFeedback.status),
                      color: '#fff',
                      fontWeight: 'bold',
                    }}
                  >
                    {selectedFeedback.status.charAt(0).toUpperCase() + selectedFeedback.status.slice(1)}
                  </span>
                </p>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <strong>Message:</strong>
                <p style={{
                  marginTop: '5px',
                  padding: '15px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '8px',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap'
                }}>
                  {selectedFeedback.message}
                </p>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <strong>Submitted On:</strong>
                <p style={{ marginTop: '5px' }}>{formatDate(selectedFeedback.createdAt)}</p>
              </div>

              {/* Admin Reply Section */}
              <div style={{
                marginTop: '20px',
                paddingTop: '20px',
                borderTop: '2px solid #FF1493',
                backgroundColor: '#FFF0F5',
                padding: '20px',
                borderRadius: '12px'
              }}>
                <strong style={{ fontSize: '16px', color: '#FF1493' }}>
                  📝 Admin Reply
                </strong>

                {selectedFeedback.adminReply && (
                  <div style={{
                    marginTop: '12px',
                    marginBottom: '15px',
                    padding: '15px',
                    backgroundColor: '#E8F5E9',
                    borderRadius: '8px',
                    borderLeft: '4px solid #4CAF50'
                  }}>
                    <div style={{
                      fontSize: '12px',
                      color: '#666',
                      marginBottom: '8px',
                      fontWeight: '600'
                    }}>
                      Previous Reply ({formatDate(selectedFeedback.repliedAt)})
                    </div>
                    <p style={{
                      margin: '0',
                      lineHeight: '1.6',
                      whiteSpace: 'pre-wrap',
                      color: '#333'
                    }}>
                      {selectedFeedback.adminReply}
                    </p>
                  </div>
                )}

                <div style={{ marginTop: '12px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: '#333'
                  }}>
                    {selectedFeedback.adminReply ? 'Update Reply:' : 'Write Reply:'}
                  </label>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply to the user here..."
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '2px solid #FFB6C1',
                      fontSize: '14px',
                      lineHeight: '1.6',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      outline: 'none',
                      transition: 'border-color 0.3s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#FF1493'}
                    onBlur={(e) => e.target.style.borderColor = '#FFB6C1'}
                  />
                  <button
                    onClick={handleReply}
                    disabled={submittingReply || !replyText.trim()}
                    className="btn btn-primary"
                    style={{
                      marginTop: '12px',
                      background: 'linear-gradient(135deg, #FF1493, #E91E63)',
                      border: 'none',
                      padding: '10px 24px',
                      fontWeight: '600',
                      opacity: (!replyText.trim() || submittingReply) ? 0.6 : 1,
                      cursor: (!replyText.trim() || submittingReply) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {submittingReply ? 'Sending...' : (selectedFeedback.adminReply ? 'Update Reply' : 'Send Reply')}
                  </button>
                  <p style={{
                    marginTop: '10px',
                    fontSize: '12px',
                    color: '#666',
                    fontStyle: 'italic'
                  }}>
                    💡 The user will see your reply in the "My Feedback" section of their mobile app
                  </p>
                </div>
              </div>

              <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #ddd' }}>
                <strong>Update Status:</strong>
                <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => {
                      handleStatusUpdate(selectedFeedback.id, 'pending');
                      setShowModal(false);
                    }}
                    className="btn btn-secondary"
                    style={{ backgroundColor: '#FFA500' }}
                  >
                    Mark as Pending
                  </button>
                  <button
                    onClick={() => {
                      handleStatusUpdate(selectedFeedback.id, 'reviewed');
                      setShowModal(false);
                    }}
                    className="btn btn-secondary"
                    style={{ backgroundColor: '#2196F3' }}
                  >
                    Mark as Reviewed
                  </button>
                  <button
                    onClick={() => {
                      handleStatusUpdate(selectedFeedback.id, 'resolved');
                      setShowModal(false);
                    }}
                    className="btn btn-primary"
                    style={{ backgroundColor: '#4CAF50' }}
                  >
                    Mark as Resolved
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FeedbackManager;
